// The other colours of this mask as round photo swatches (owner, 2026-10-17; snippets/sa-color-siblings.liquid).
// Every colour is its own product. The masks whose English title has the same name before " - " are one model, read
// from the Storefront API (Liquid cannot list 290 masks on a product page) and kept in this tab for 10 minutes.
// As on Allbirds:
// - hovering a swatch shows its colour name in the label, and leaving shows this mask's colour again;
// - a tap opens that colour in place of this page in history, so Back returns to where the shopper came from;
// - a size the shopper picked or measured by hand comes along as ?variant=, which the purchase controller admits from
//   store links (global.js admitSize). A camera-measured size is not sent: the remembered camera measurement preselects
//   it on the next mask with its own caption. A hand measurement brings only its size to the other colours of the same
//   model, not its card (that stays on its page, owner 10 Oct), and its "Manual guide viewed" order attribution
//   (size-selection-attribution.js) comes along with it (owner, 17 Oct).
(() => {
  const TOKEN = '33a4ab3e489993e75e1cfdb0e4d574bd';
  const API = '/api/2025-07/graphql.json';
  const TTL = 10 * 60 * 1000;
  const GAP = 6;
  const SHOWN = 'width=96&height=96&crop=center';

  const titleOf = text => String(text || '').replace(/ [—–] /g, ' - ');
  const modelOf = text => titleOf(text).split(' - ')[0].trim();
  const colourOf = text => titleOf(text).split(' - ').slice(1).join(' - ').trim();
  // A title without its colour (an older listing) is named by its colour filter instead, e.g. "Red/Black", never blank.
  const familyOf = node => {
    try { return (JSON.parse(node.colorFamily?.value || '[]') || []).join('/'); } catch (error) { return ''; }
  };

  function read(key) {
    try {
      const saved = JSON.parse(sessionStorage.getItem(key));
      return saved && Date.now() - saved.at < TTL ? saved.value : null;
    } catch (error) { return null; }
  }
  function keep(key, value) {
    try { sessionStorage.setItem(key, JSON.stringify({at: Date.now(), value})); } catch (error) {}
  }

  async function request(query, variables) {
    const response = await fetch(API, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': TOKEN},
      body: JSON.stringify({query, variables})
    });
    if (!response.ok) throw new Error(`storefront ${response.status}`);
    const json = await response.json();
    if (!json.data) throw new Error('storefront data');
    return json.data;
  }

  const SEARCH = context => `query($q:String!)${context}{products(first:100,query:$q,sortKey:BEST_SELLING){nodes{id handle title featuredImage{url} colorFamily:metafield(namespace:"custom",key:"color_family"){value} variants(first:60){nodes{id availableForSale selectedOptions{name value}}}}}}`;

  async function load(root) {
    const {handle, language, country} = root.dataset;
    // The Storefront API answers in the language of the shopper's session unless told otherwise, so the English
    // lookups name English explicitly (a French visitor got "Masque Oni Cyber" and no swatches before).
    const englishContext = '@inContext(language:EN)';
    let model = root.dataset.model || read(`sa-colors-model-3:${handle}`);
    if (!model) {
      const data = await request(`query($h:String!)${englishContext}{product(handle:$h){title}}`, {h: handle});
      model = modelOf(data.product?.title);
    }
    if (!model) return null;
    const key = `sa-colors-4:${language}:${country}:${model}`;
    const cached = read(key);
    if (cached) return cached;
    const shopperLanguage = /^[A-Z]{2}(_[A-Z]{2})?$/.test(language) ? language : 'EN';
    const english = shopperLanguage === 'EN';
    const context = `@inContext(${/^[A-Z]{2}$/.test(country) ? `country:${country},` : ''}language:${shopperLanguage})`;
    const variables = {q: `title:"${model.replace(/"/g, '')}"*`};
    const [shown, base] = await Promise.all([
      request(SEARCH(context), variables),
      english ? null : request(`query($q:String!)${englishContext}{products(first:100,query:$q){nodes{handle title}}}`, variables)
    ]);
    // The English title decides the model; the shopper's language gives the colour names.
    const englishTitles = new Map((base || shown).products.nodes.map(node => [node.handle, node.title]));
    const items = shown.products.nodes
      .filter(node => modelOf(englishTitles.get(node.handle)) === model)
      .map(node => ({
        id: node.id.split('/').pop(),
        handle: node.handle,
        colour: colourOf(node.title) || colourOf(englishTitles.get(node.handle)) || familyOf(node),
        image: node.featuredImage?.url || '',
        variants: node.variants.nodes.map(variant => ({
          id: variant.id.split('/').pop(),
          available: variant.availableForSale,
          options: variant.selectedOptions
        }))
      }));
    keep(key, items);
    items.forEach(item => keep(`sa-colors-model-3:${item.handle}`, model));
    return items;
  }

  // The size this shopper picked or measured by hand on this mask, or null (see the note at the top).
  function handPickedSize() {
    const info = document.querySelector('face-fit-scanner')?.closest('product-info');
    const purchase = info?.faceFitPurchase;
    if (!purchase || purchase.bundleSizes || purchase.rememberedApplied) return null;
    const size = purchase.size;
    if (!size || !purchase.choseSize(purchase.sizeIndex, size)) return null;
    const rules = window.SpaceArmoryFitRules;
    const aliases = typeof spaceArmorySizeAliases === 'object' ? spaceArmorySizeAliases : {};
    const gives = record => !!record && typeof record.cm === 'number' && (aliases[rules?.sizeFor(record.cm)] || []).includes(size.trim().toLowerCase());
    if ([purchase.freshMeasurement, purchase.rememberedMeasurement, purchase.savedMeasurement].some(gives)) return null;
    const others = new Map(purchase.names.map((name, index) => [String(name).trim().toLowerCase(), purchase.values[index]]));
    return {size: size.trim().toLowerCase(), others, guide: gives(purchase.manualMeasurement) || manualGuideSeen(purchase.root?.dataset.productId)};
  }

  // That size in the other colour, with this page's other choices (fangs, strap) where the colour has them.
  function variantFor(item, picked) {
    let best = null;
    let bestScore = -1;
    for (const variant of item.variants) {
      if (!variant.options.some(option => option.value.trim().toLowerCase() === picked.size)) continue;
      let score = variant.available ? 100 : 0;
      for (const option of variant.options) {
        if (picked.others.get(option.name.trim().toLowerCase()) === option.value) score++;
      }
      if (score > bestScore) { best = variant; bestScore = score; }
    }
    return best;
  }

  // The carried size counts as the shopper's own pick on the next colour too (the purchase controller's per-product
  // record, global.js chosenSizes), so it keeps following from colour to colour, and a reload there keeps it.
  function manualGuideSeen(id) {
    try { return JSON.parse(sessionStorage.getItem(`sa_sizing_guides_v1:${id}`))?.manual === true; } catch (error) { return false; }
  }
  function keepChosen(item, carried) {
    const index = carried.variant.options.findIndex(option => option.value.trim().toLowerCase() === carried.size);
    if (index < 0 || !item.id) return;
    if (carried.guide) {
      try {
        const guides = JSON.parse(sessionStorage.getItem(`sa_sizing_guides_v1:${item.id}`) || '{}') || {};
        sessionStorage.setItem(`sa_sizing_guides_v1:${item.id}`, JSON.stringify({camera: guides.camera === true, manual: true}));
      } catch (error) {}
    }
    const key = `sa-chosen-sizes:${item.id}`;
    try {
      const chosen = JSON.parse(sessionStorage.getItem(key) || '{}');
      const record = chosen && typeof chosen === 'object' && !Array.isArray(chosen) ? chosen : {};
      record[index] = carried.variant.options[index].value;
      sessionStorage.setItem(key, JSON.stringify(record));
    } catch (error) {}
  }

  function render(root, items) {
    const current = items.find(item => item.handle === root.dataset.handle);
    if (items.length < 2 || !current) return;
    const routes = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = item => `${routes}products/${item.handle}`;

    const head = document.createElement('div');
    head.className = 'sa-colors__head';
    const label = document.createElement('span');
    label.className = 'sa-colors__label';
    label.append(`${root.dataset.label} `);
    const value = document.createElement('span');
    value.className = 'sa-colors__value';
    value.textContent = current.colour;
    label.append(value);
    const count = document.createElement('span');
    count.className = 'sa-colors__count';
    count.textContent = root.dataset.count.replace('__N__', items.length);
    head.append(label, count);

    const list = document.createElement('ul');
    list.className = 'sa-colors__list';
    list.setAttribute('role', 'list');
    list.setAttribute('aria-label', root.dataset.list);

    const prefetched = new Set();
    const prefetch = href => {
      if (prefetched.has(href)) return;
      prefetched.add(href);
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = href;
      document.head.append(link);
    };

    for (const item of items) {
      const li = document.createElement('li');
      li.className = 'sa-colors__item';
      const link = document.createElement('a');
      link.className = 'sa-colors__link';
      link.href = url(item);
      link.setAttribute('aria-label', item.colour);
      const img = document.createElement('img');
      img.alt = '';
      img.width = 96;
      img.height = 96;
      img.loading = 'lazy';
      img.decoding = 'async';
      if (item.image) img.src = item.image + (item.image.includes('?') ? '&' : '?') + SHOWN;
      link.append(img);
      li.append(link);
      list.append(li);
      if (item === current) {
        link.setAttribute('aria-current', 'page');
        link.addEventListener('click', event => event.preventDefault());
        continue;
      }
      // The link carries the hand-picked size from the moment the pointer or focus reaches it.
      let carried = null;
      const aim = () => {
        const picked = handPickedSize();
        const variant = picked && variantFor(item, picked);
        carried = variant ? {variant, size: picked.size, guide: picked.guide} : null;
        link.href = variant ? `${url(item)}?variant=${variant.id}` : url(item);
        return link.href;
      };
      const show = () => { value.textContent = item.colour; };
      const back = () => { value.textContent = current.colour; };
      link.addEventListener('mouseenter', () => { show(); prefetch(aim()); });
      link.addEventListener('mouseleave', back);
      link.addEventListener('focus', () => { show(); aim(); });
      link.addEventListener('blur', back);
      link.addEventListener('touchstart', () => prefetch(aim()), {passive: true});
      link.addEventListener('click', event => {
        const href = aim();
        if (carried) keepChosen(item, carried);
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        location.replace(href);
      });
    }

    const moreItem = document.createElement('li');
    moreItem.className = 'sa-colors__item';
    const more = document.createElement('button');
    more.type = 'button';
    more.className = 'sa-colors__more';
    more.setAttribute('aria-expanded', 'false');
    moreItem.append(more);
    list.append(moreItem);

    root.replaceChildren(head, list);
    root.hidden = false;

    const swatches = () => Array.from(list.querySelectorAll('.sa-colors__link'), link => link.parentElement);
    // Closed: the colours that fit on one line and "+N". Open: every colour and a last "−" circle that closes the list
    // again, the same on phones and desktops (owner, 17 Oct).
    const layout = () => {
      const all = swatches();
      all.forEach(li => { li.hidden = false; });
      moreItem.hidden = false;
      const padding = parseFloat(getComputedStyle(list).paddingLeft) + parseFloat(getComputedStyle(list).paddingRight);
      const width = list.clientWidth - padding;
      const circle = more.offsetWidth || 42; // offsetWidth: unaffected by the fade's scale
      const fit = Math.max(2, Math.floor((width + GAP) / (circle + GAP)));
      if (all.length <= fit) { moreItem.hidden = true; root.classList.remove('is-open'); return; }
      if (root.classList.contains('is-open')) {
        more.textContent = '\u2212';
        more.setAttribute('aria-expanded', 'true');
        more.setAttribute('aria-label', root.dataset.showFewer || 'Show fewer colors');
        return;
      }
      more.setAttribute('aria-expanded', 'false');
      more.setAttribute('aria-label', root.dataset.showAll.replace('__N__', all.length));
      const visible = fit - 1;
      // This mask's colour always shows: it takes the last place before "+N".
      const mine = all.find(li => li.querySelector('[aria-current]'));
      if (all.indexOf(mine) >= visible) {
        list.insertBefore(mine, all[visible - 1]);
        return layout();
      }
      all.forEach((li, index) => { li.hidden = index >= visible; });
      more.textContent = `+${all.length - visible}`;
    };
    // Opening eases the list's height open (260 ms) while the colours that come in fade in (160 ms); closing fades the
    // extra colours out (160 ms) and then eases the height back: the timings of the Measure Again swap (owner, 10 Oct).
    // With reduced motion the list just switches. Taps while it moves are ignored (owner, 17 Oct).
    const reduced = () => !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const ease = t => 1 - Math.pow(1 - t, 3);
    const tween = (duration, frame) => new Promise(resolve => {
      const start = performance.now();
      const step = now => {
        // A frame's timestamp can come before start: never run the tween backwards.
        const t = Math.min(1, Math.max(0, (now - start) / duration));
        frame(ease(t));
        if (t < 1) requestAnimationFrame(step); else resolve();
      };
      requestAnimationFrame(step);
    });
    const fade = (elements, from, to, delay = () => 0) => Promise.all(elements.map((element, index) => element.animate(
      [{opacity: from, transform: from ? 'none' : 'scale(0.86)'}, {opacity: to, transform: to ? 'none' : 'scale(0.86)'}],
      {duration: 160, delay: delay(index), easing: 'ease-out', fill: 'both'}
    ).finished.catch(() => {})));
    const settleFade = elements => elements.forEach(element => element.getAnimations().forEach(animation => animation.cancel()));
    let busy = false;
    more.addEventListener('click', async event => {
      if (busy) return;
      const motion = !reduced();
      // The page never scrolls: the list grows or shrinks in place and what is below it moves (owner, 17 Oct: scrolling
      // the circle back under the finger made the page jump on iPhone). Browser scroll anchoring is off meanwhile, so
      // Chrome does not scroll on its own either.
      const from = list.getBoundingClientRect().height;
      const anchor = document.documentElement.style.overflowAnchor;
      document.documentElement.style.overflowAnchor = 'none';
      busy = motion;
      root.classList.toggle('is-moving', motion);
      try {
        if (!root.classList.contains('is-open')) {
          // Keyboard users go on from the first colour that was hidden.
          const coming = swatches().filter(li => li.hidden);
          const next = coming[0]?.querySelector('a');
          root.classList.add('is-open');
          layout();
          if (motion) {
            const to = list.getBoundingClientRect().height;
            const fading = fade([...coming, moreItem], 0, 1, index => 40 + Math.min(index, 24) * 8);
            list.style.height = `${from}px`;
            await tween(260, k => { list.style.height = `${from + (to - from) * k}px`; });
            list.style.height = '';
            await fading;
            settleFade([...coming, moreItem]);
          }
          if (event.detail === 0) next?.focus();
          return;
        }
        // Which colours leave: lay the closed list out once, unseen, and back.
        root.classList.remove('is-open');
        layout();
        const leaving = swatches().filter(li => li.hidden);
        root.classList.add('is-open');
        layout();
        if (motion) await fade([...leaving, moreItem], 1, 0);
        root.classList.remove('is-open');
        layout();
        settleFade([...leaving, moreItem]);
        const to = list.getBoundingClientRect().height;
        if (motion) list.style.height = `${from}px`;
        if (motion) {
          const back = fade([moreItem], 0, 1);
          await tween(260, k => { list.style.height = `${from + (to - from) * k}px`; });
          list.style.height = '';
          await back;
          settleFade([moreItem]);
        }
      } finally {
        list.style.height = '';
        document.documentElement.style.overflowAnchor = anchor;
        root.classList.remove('is-moving');
        busy = false;
      }
    });
    layout();
    if ('ResizeObserver' in window) {
      let lastWidth = list.clientWidth;
      new ResizeObserver(() => {
        if (list.clientWidth === lastWidth) return;
        lastWidth = list.clientWidth;
        layout();
      }).observe(list);
    }
  }

  function start() {
    document.querySelectorAll('[data-sa-colors]:not([data-sa-colors-ready])').forEach(root => {
      root.setAttribute('data-sa-colors-ready', '');
      load(root).then(items => items && render(root, items)).catch(() => {});
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();
})();
